/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-undef */
/* eslint-disable import/no-unresolved */
import { memo, useState, useEffect } from "react";
import { FormikProps } from "formik";
import { Button } from "@mui/material";
import { useMutation } from "react-query";
import { recalculatePremium } from "src/services/LeadInfo";
import store from "src/redux/store";
import {
  SET_TOAST_ERROR,
  setRecalculateStatus,
  setRecalculatePremiumError
} from "src/redux/Actions/common";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import CustomBackDrop from "src/shared/CustomBackDrop";
import { loanDetailsUpdate } from "src/services/FormDetails";
import { findKeysByValue, mapTouched } from "src/helpers/FormDetails/ProposalDetails/resources";
import { prepareRiderPayload } from "src/helpers/FormDetails/resources";
import { useRiderDetailsMutation } from "src/utils/hooks/useSaveRiderMutation";
import CustomSelect from "../../../../../shared/CustomSelect";
import { LoanDetails } from "../../../../../helpers/FormDetails/ProposalDetails/interfaces";

interface Props {
  formik: FormikProps<LoanDetails>;
}
function PremiumFunding({
  formik: { errors, setFieldTouched, touched, values, handleChange, setFieldValue, setTouched }
}: Props) {
  const leadId = useAppSelector((state) => state.common.leadId);
  const [latestPremium, setLatestPremium] = useState<number>();
  const [riderPremium, setRiderPremium] = useState<number>();
  const [totalPremium, setTotalPremium] = useState<number>();
  const premiumFunding = useAppSelector((state) => state.masterdata_reducer?.funding);
  const masterReducer = useAppSelector((state) => state.masterdata_reducer);
  const isProportionateLead = useAppSelector((state) => state.common?.isProportionateLead);
  const isRiderOpted = useAppSelector((state) => state.loanDetails?.isRiderSelectionLocked);
  const { planDetails: { premiumFunding: premiumFundingField } = {} as any } =
    useAppSelector((state): any => state.masterdata_reducer?.mefJourneySchema) || {};

  const { mutate: mutateRecalculatePremium, isLoading: isRecalculatePremiumLoading } = useMutation(
    ["recalculatePremium"],
    recalculatePremium,
    {
      retry: false,
      onSuccess: (response) => {
        const basePremium = response.data?.modalPremIncTax || 0;
        const riderPremiumValue = response.data?.riderPremium || 0;
        if (isRiderOpted) {
          setRiderPremium(riderPremiumValue?.toLocaleString("en-IN"));
          setTotalPremium(response.data?.totalPremium?.toLocaleString("en-IN"));
          setFieldValue("premiumDetails.riderPremium", riderPremiumValue);
        }
        setLatestPremium(basePremium?.toLocaleString("en-IN"));
        setFieldValue("premiumDetails.totalPayment", basePremium);
        setFieldValue("plan.deathSumInsured", response.data?.deathSumAssured || "");
        store.dispatch(setRecalculateStatus(true));
        store.dispatch(setRecalculatePremiumError(""));
        store.dispatch(
          SET_TOAST_ERROR({
            error: true,
            duration: 4000,
            type: "success",
            message: "Latest Premium Fetched"
          })
        );
      },
      onError: (error: any) => {
        setRiderPremium(0);
        setTotalPremium(0);
        setLatestPremium(0);
        setFieldValue("premiumDetails.riderPremium", 0);
        store.dispatch(setRecalculateStatus(false));
        if (error?.message !== "Internal Server Error")
          store.dispatch(setRecalculatePremiumError(error?.message));
        store.dispatch(
          SET_TOAST_ERROR({
            error: true,
            duration: 2000,
            message: "Unable to fetch premium"
          })
        );
      }
    }
  );

  const { mutate: mutateRiderDetails, isLoading: isRiderLoading } = useRiderDetailsMutation(
    setFieldValue,
    values?.riderDetails
  );

  const { mutate: mutateLoanDetails, isLoading: isSaveLoanLoading } = useMutation(
    loanDetailsUpdate,
    {
      onSuccess: () => {
        const riderPayload = prepareRiderPayload(leadId, values?.riderDetails);
        if (isRiderOpted) {
          mutateRiderDetails(riderPayload, {
            onSuccess: () => {
              mutateRecalculatePremium(leadId);
            },
            onError: (riderErrorResponse) => {
              store.dispatch(
                SET_TOAST_ERROR({
                  error: true,
                  type: "error",
                  duration: 2000,
                  message: riderErrorResponse?.message || "Unable to save rider. Please try again"
                })
              );
            }
          });
        } else {
          mutateRecalculatePremium(leadId);
        }
      }
    }
  );
  const handleRecalculatePremium = () => {
    if (Object.keys(errors).filter((ele) => ele !== "existingPolicy").length) {
      const { existingPolicy, ...rest } = values;
      const allTouched = mapTouched(rest, setFieldTouched);
      setTouched(allTouched, true);
      store.dispatch(
        SET_TOAST_ERROR({
          error: true,
          duration: 4000,
          message: "Please Fill All The Mandatory Fields"
        })
      );
    } else mutateLoanDetails({ values, section: "loan", leadId, masterReducer });
  };

  useEffect(() => {
    setFieldValue(
      "premiumFunding.premiumFundingCode",
      findKeysByValue(premiumFunding, values.premiumFunding?.premiumFunding) || ""
    );
  }, [values.premiumFunding?.premiumFunding]);

  return (
    <div>
      <CustomBackDrop open={isRecalculatePremiumLoading || isSaveLoanLoading || isRiderLoading} />
      <span className="font-montserrat text-black text-base font-bold mt-8 mb-6 flex">
        Premium Funding
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
        {premiumFundingField?.visible && (
          <CustomSelect
            requiredStar={premiumFundingField?.required}
            disabledField={premiumFundingField?.disabled || isProportionateLead}
            label={premiumFundingField?.label}
            options={Object.values(premiumFunding)}
            onChange={handleChange}
            setFieldTouched={setFieldTouched}
            name="premiumFunding.premiumFunding"
            value={values.premiumFunding.premiumFunding}
            error={errors.premiumFunding?.premiumFunding}
            touched={touched.premiumFunding?.premiumFunding}
          />
        )}
        <Button
          variant="contained"
          classes={{ contained: "!rounded-lg !text-xs !h-12.5" }}
          onClick={handleRecalculatePremium}
          className="!uppercase !bg-mediumElectricBlue"
        >
          Recalculate Premium
        </Button>
      </div>

      <div className="flex gap-x-7 mt-6">
        {!!latestPremium && (
          <div className="!h-12.5 px-3 font-montserrat text-sm text-black flex items-center justify-center font-medium border-2 border-hotShot rounded-lg whitespace-nowrap">
            <span className="mr-1">Base Premium: Rs. {latestPremium}</span>
            <span className="italic text-gray-500 ml-1 text-xs">(incl. GST)</span>
          </div>
        )}
        {!!isRiderOpted && !!riderPremium && (
          <div className="!h-12.5 px-3 font-montserrat text-sm text-black flex items-center justify-center font-medium border-2 border-hotShot rounded-lg whitespace-nowrap">
            <span className="mr-1">Rider Premium: Rs. {riderPremium}</span>
            <span className="italic text-gray-500 ml-1 text-xs">(incl. GST)</span>
          </div>
        )}
        {!!isRiderOpted && !!totalPremium && (
          <div className="!h-12.5 px-3 font-montserrat text-sm text-black flex items-center justify-center font-medium border-2 border-hotShot rounded-lg whitespace-nowrap">
            <span className="mr-1">Total Premium: Rs. {totalPremium}</span>
            <span className="italic text-gray-500 ml-1 text-xs">(incl. GST)</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(PremiumFunding);
